import { CropCoords, CropOptions, ImageOptions } from "../types"
import { UseStateSetter } from "../../../../types/react"
import type { Crop } from "react-image-crop"
import { loadImageAsync } from "../../../../utils/loadImage"
import { canvasToBlob } from "../../../../utils/canvas"
import {
    CroppedVariantEdit,
    CropVariantResult,
    ImageCropEditData,
    ImageCropInputData,
    ImageCropResultData,
} from "./types"
import {
    fillTransparentBackground,
    getAspectRatioFromCropType,
    getOutputFileName,
    pica,
    resolveOutputType,
} from "../utils"
import { logger } from "../../../../utils/loggerWrap"

export const prepareEditData = (imgFiles: ImageCropInputData[], cropOptions: CropOptions[]): ImageCropEditData[] => {
    return imgFiles.map(img => {
        return {
            file: img.file,
            url: URL.createObjectURL(img.file),
            croppedVariants: cropOptions.map(o => {
                const variantData = img.croppedVariants?.find(v => v.cropType === o.cropType) ?? null
                return {
                    cropType: o.cropType,
                    cropCoords: variantData?.cropCoords ?? null,
                    cropOption: o,
                    cropData: null, // will be set when user crops the image
                }
            }),
        }
    })
}

export const getCurrentCropEditData = (
    imageCropData: ImageCropEditData[],
    currentImgIdx: number,
    currentCropRatioIdx: number
) => {
    const currentImage = imageCropData[currentImgIdx]
    const currentCropVariant = currentImage ? currentImage.croppedVariants?.[currentCropRatioIdx] : null

    return {
        currentImage: currentImage,
        currentFile: currentImage?.file ?? null,
        currentImgUrl: currentImage?.url,
        currentCropVariant: currentCropVariant,
        currentCropOption: currentCropVariant?.cropOption ?? null,
        currentCropData: currentCropVariant?.cropData ?? null,
        currentCropCoords: currentCropVariant?.cropCoords ?? null,
        currentAspect: currentCropVariant ? getAspectRatioFromCropType(currentCropVariant.cropType) : null,
    }
}

export const updateCropImagesState = (
    newData: Partial<CroppedVariantEdit>,
    imageIdx: number,
    cropIdx: number,
    updateStateData: UseStateSetter<ImageCropEditData[]>
) => {
    updateStateData(prevState => {
        return prevState.map((img, imgIdx) => {
            if (imgIdx !== imageIdx) {
                return img
            }

            const newVariants = img.croppedVariants?.map((v, vIdx) => {
                if (vIdx !== cropIdx) {
                    return v
                }
                return {
                    ...v,
                    ...newData,
                }
            })
            return {
                ...img,
                croppedVariants: newVariants,
            }
        })
    })
}

export const initCropAreaDimensions = (
    cropOption: CropOptions,
    savedCropCoords: CropCoords | null,
    imgWidth: number,
    imgHeight: number
) => {
    const defaultCropArea = {
        width: cropOption.defaultWidth ?? imgWidth,
        height: cropOption.defaultHeight ?? imgHeight,
        x: cropOption.defaultX ?? 0,
        y: cropOption.defaultY ?? 0,
    }

    // Use saved crop area if exists, otherwise fallback to default crop area parameters
    let defaultWidth = savedCropCoords?.width ?? defaultCropArea.width
    let defaultHeight = savedCropCoords?.height ?? defaultCropArea.height
    let defaultX = savedCropCoords?.x ?? defaultCropArea.x
    let defaultY = savedCropCoords?.y ?? defaultCropArea.y

    // Make sure width/height/x/y does not exceed image dimensions
    defaultWidth = defaultWidth > imgWidth ? imgWidth : defaultWidth
    defaultHeight = defaultHeight > imgHeight ? imgHeight : defaultHeight
    defaultX = defaultX > imgWidth ? imgWidth : defaultX
    defaultY = defaultY > imgHeight ? imgHeight : defaultY

    // Convert pixel values to percentages for 'react-image-crop' component
    const cropWidthPerc = (defaultWidth / imgWidth) * 100
    const cropHeightPerc = (defaultHeight / imgHeight) * 100
    const cropXPerc = (defaultX / imgWidth) * 100
    const cropYPerc = (defaultY / imgHeight) * 100

    return {
        cropWidthPerc: cropWidthPerc,
        cropHeightPerc: cropHeightPerc,
        cropXPerc: cropXPerc,
        cropYPerc: cropYPerc,
    }
}

// Missing or non-positive limit means "no limit"
const toDimensionLimit = (value?: number) => (value && value > 0 ? value : Infinity)

export const downscaleCanvas = (
    canvas: HTMLCanvasElement,
    maxWidth?: number,
    maxHeight?: number
): Promise<HTMLCanvasElement> => {
    const ratio = Math.min(
        1,
        toDimensionLimit(maxWidth) / canvas.width,
        toDimensionLimit(maxHeight) / canvas.height
    )
    if (ratio === 1) {
        return Promise.resolve(canvas)
    }

    const target = document.createElement("canvas")
    target.width = Math.max(1, Math.round(canvas.width * ratio))
    target.height = Math.max(1, Math.round(canvas.height * ratio))

    return pica.resize(canvas, target)
}

export const getCropOutput = async (
    cropArea: Crop,
    currentFile: File,
    currentImgUrl: string,
    cropOption: CropOptions | null,
    imageOptions: ImageOptions | null
): Promise<Omit<CropVariantResult, "cropType"> | null> => {
    try {
        const image = await loadImageAsync(currentImgUrl)

        const imgCropX = image.width * (cropArea.x / 100)
        const imgCropY = image.height * (cropArea.y / 100)
        const imgCropWidth = image.width * (cropArea.width / 100)
        const imgCropHeight = image.height * (cropArea.height / 100)

        const canvas = document.createElement("canvas")
        canvas.width = imgCropWidth
        canvas.height = imgCropHeight

        const ctx = canvas.getContext("2d")
        if (ctx) {
            ctx.drawImage(image, imgCropX, imgCropY, imgCropWidth, imgCropHeight, 0, 0, imgCropWidth, imgCropHeight)

            const outputCanvas = await downscaleCanvas(canvas, cropOption?.maxWidth, cropOption?.maxHeight)
            const outputType = resolveOutputType(currentFile.type, imageOptions?.forceJpeg)
            if (outputType === "image/jpeg") {
                fillTransparentBackground(outputCanvas)
            }
            const blob = await canvasToBlob(outputCanvas, outputType, 0.75)
            const fileName = getOutputFileName(currentFile.name, currentFile.type, outputType)
            const file = new File([blob], fileName, { type: outputType })

            const coords: CropCoords = {
                x: Math.round(imgCropX),
                y: Math.round(imgCropY),
                width: Math.round(imgCropWidth),
                height: Math.round(imgCropHeight),
            }

            return {
                file: file,
                cropCoords: coords,
                width: outputCanvas.width,
                height: outputCanvas.height,
            }
        }
        throw new Error("Could not get canvas context")
    } catch (err) {
        logger.error("Error generating cropped image", err)
        return null
    }
}

export const mutateCropResultData = (
    data: ImageCropResultData[],
    imageIdx: number,
    cropIdx: number,
    originalFile: File,
    cropVariantData: CropVariantResult
): void => {
    // Data can be empty []
    data[imageIdx] = {
        file: originalFile,
        croppedVariants: data[imageIdx]?.croppedVariants ?? [],
    }

    // Update or add cropped variant data
    data[imageIdx].croppedVariants[cropIdx] = cropVariantData
}
